import { useState, useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowUpRight, Check } from "lucide-react";
import axios from "axios";
import { SEO, buildProductJsonLd } from "@/components/SEO";
import { HoverImage } from "@/components/HoverImage";
import { HeroImage } from "@/components/HeroImage";
import { EnquiryForm } from "@/components/EnquiryForm";
import { BuyNowButton } from "@/components/BuyNowButton";
import { AddToCartButton } from "@/components/AddToCartButton";
import { ProductOptions } from "@/components/ProductOptions";
import { useRecentlyViewed } from "@/hooks/useRecentlyViewed";
import { RecentlyViewedStrip } from "@/components/RecentlyViewedStrip";
import { ProductGallery, normalizeGalleryImage } from "@/components/ProductGallery";

const API = process.env.REACT_APP_BACKEND_URL;
const BULL_BAR_VEHICLES = [
  { key: "lc70", label: "Land Cruiser 70 Series", short: "Land Cruiser 70 Series", note: "70 · 76 · 78 · 79 · All years — select year at checkout" },
  { key: "hilux", label: "Toyota Hilux", short: "Hilux (2016-2025)", note: "2016 to 2025" },
  { key: "ranger", label: "Ford Ranger", short: "Ranger (2022+)", note: "New-Gen 2022+" },
  { key: "jimny", label: "Suzuki Jimny", short: "Jimny", note: "Bars, sliders, ladders" },
  { key: "gwm-cannon", label: "GWM Cannon", short: "GWM Cannon", note: "Cannon & Alpha" },
  { key: "byd-shark", label: "BYD Shark", short: "BYD Shark", note: "PHEV ute" },
  { key: "gladiator", label: "Jeep Gladiator", short: "Gladiator", note: "New for NZ" },
  { key: "lc200", label: "Land Cruiser 200 Series", short: "LC 200 Series", note: "ONCA barwork · built to order" },
  { key: "lc300", label: "Land Cruiser 300 Series", short: "LC 300 Series", note: "ONCA barwork · built to order" },
];
const fmt = (n) => n?.toLocaleString("en-NZ");

export default function BullBarDetailPage() {
  const { slug } = useParams();
  const [models, setModels] = useState(null); // null = still loading

  useEffect(() => {
    axios.get(`${API}/api/products/bullbar`).then((res) => setModels(res.data)).catch((err) => { console.error(err); setModels([]); });
  }, []);

  const model = models?.find((m) => m.slug === slug);
  const hasOptions = model?.optionGroups?.length > 0;
  const [optionsState, setOptionsState] = useState({ selectedOptions: {}, totalPrice: model?.price || 0, allRequiredSelected: !hasOptions, combinationInStock: true });
  const recentlyViewed = useRecentlyViewed(
    model && { path: `/bull-bars/${model.slug}`, name: model.name, thumb: model.thumb, price: model.price, priceFrom: model.priceFrom, priceOnRequest: model.priceOnRequest }
  );

  if (models === null) {
    return <div className="min-h-[60vh] flex items-center justify-center text-white/50 font-mono text-sm">Loading…</div>;
  }
  if (!model) return <Navigate to="/bull-bars" replace />;

  const canBuyNow = !model.priceOnRequest && model.inStock !== false;
  const vehicle = BULL_BAR_VEHICLES.find((v) => v.key === model.vehicle);
  const related = models.filter((m) => m.vehicle === model.vehicle && m.slug !== model.slug).slice(0, 3);
  const gallery = model.gallery && model.gallery.length > 0 ? model.gallery.map(normalizeGalleryImage) : [{ src: model.thumb, alt: null, type: "image", pos: "center" }];

  return (
    <div data-testid="bull-bar-detail-page">
      <SEO
        path={`/bull-bars/${model.slug}`}
        title={`${model.name}${vehicle ? ` — ${vehicle.label}` : ""} · NZ`}
        description={`${model.name} — ${model.tagline}. ${model.intro.slice(0, 140)}`}
        image={model.hero || model.thumb}
        jsonLd={buildProductJsonLd({ name: model.name, description: model.tagline, image: model.hero || model.thumb, price: model.price, priceOnRequest: model.priceOnRequest, inStock: model.inStock, url: `/bull-bars/${model.slug}` })}
      />

      <section className="relative min-h-[60svh] flex items-end overflow-hidden">
        <div className="absolute inset-0">
          <HeroImage src={model.hero || model.thumb} alt={model.name} className="w-full h-full" desktopPosition={model.heroPosition || "50% center"} mobilePosition={model.heroFocalMobile} />
          <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-main)] from-8% via-black/40 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/20 to-transparent md:via-transparent" />
        </div>
        <div className="relative z-10 container-x pb-16 pt-32 w-full">
          <div className="flex items-center gap-3 mb-6 flex-wrap">
            <Link to="/bull-bars" data-testid="bullbar-breadcrumb" className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 hover:text-[var(--brand-primary)]">
              ← Bull Bars
            </Link>
            {vehicle && (
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/80 bg-black/60 backdrop-blur-sm px-2.5 py-1">
                {vehicle.short}
              </span>
            )}
            {model.badge && (
              <span className="font-mono text-[10px] tracking-[0.25em] uppercase text-black bg-[var(--brand-primary)] px-2.5 py-1">
                {model.badge}
              </span>
            )}
          </div>
          <h1 className="font-display text-4xl md:text-6xl lg:text-7xl leading-[0.9] text-white hero-text-shadow max-w-4xl">
            {model.name.toUpperCase()}
          </h1>
          <p className="text-base md:text-lg text-[var(--brand-primary)] font-mono uppercase tracking-widest mt-5">
            {model.tagline}
          </p>
          <div className="mt-8 flex items-start gap-6 flex-wrap">
            <div>
              <p className="font-display text-4xl md:text-5xl text-white leading-none">
                {model.priceOnRequest ? (
                  <span className="text-2xl font-mono uppercase tracking-widest text-white/80">Enquire For Price</span>
                ) : (
                  <>
                    {(model.priceFrom && !hasOptions) && <span className="text-lg font-mono uppercase tracking-widest text-white/70 mr-2">FROM</span>}
                    ${fmt(hasOptions ? optionsState.totalPrice : model.price)}
                  </>
                )}
              </p>
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/60 mt-2">
                {model.priceOnRequest ? "" : "GST included · "}Availability: {model.availability}
              </p>
            </div>
            <div className="flex items-start gap-3 flex-wrap">
              {canBuyNow && (
                <>
                  <BuyNowButton
                    productPath={`/bull-bars/${model.slug}`}
                    testId="bullbar-cta-buy-now"
                    selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                    disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                    disabledHint={hasOptions && optionsState.allRequiredSelected && !optionsState.combinationInStock ? "" : "Select all options below to continue"}
                  />
                  <AddToCartButton
                    productPath={`/bull-bars/${model.slug}`}
                    testId="bullbar-cta-add-to-cart"
                    name={model.name}
                    thumb={model.thumb}
                    unitPrice={hasOptions ? optionsState.totalPrice : model.price}
                    priceFrom={model.priceFrom && !hasOptions}
                    selectedOptions={hasOptions ? optionsState.selectedOptions : null}
                    disabled={hasOptions && (!optionsState.allRequiredSelected || !optionsState.combinationInStock)}
                  />
                </>
              )}
              <a href="#enquire" data-testid="bullbar-cta-enquire" className="btn-secondary">Enquire About This <ArrowUpRight size={14} /></a>
            </div>
          </div>
          {model.priceOnRequest && (
            <p data-testid="bullbar-made-to-order-note" className="mt-4 font-mono text-xs tracking-[0.1em] uppercase text-[var(--brand-primary)] bg-black/50 backdrop-blur-sm border border-[var(--brand-primary)]/30 inline-flex items-center px-3 py-2 max-w-full">
              Made to order — enquire for next availability
            </p>
          )}
          {hasOptions && (
            <div className="mt-8 max-w-lg">
              <ProductOptions optionGroups={model.optionGroups} variantStock={model.variantStock || []} basePrice={model.price} onChange={setOptionsState} />
            </div>
          )}
        </div>
      </section>

      <section className="container-x py-16 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
          <div className="lg:col-span-7">
            <ProductGallery gallery={gallery} productName={model.name} />
          </div>
          <div className="lg:col-span-5 space-y-8">
            <div>
              <p className="overline mb-4">— About This Bar</p>
              <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed">{model.intro}</p>
            </div>
            {model.features && model.features.length > 0 && (
              <div className="border-t border-[var(--border-default)] pt-8">
                <p className="overline mb-5">— Key Features</p>
                <ul className="space-y-3">
                  {model.features.map((f, i) => (
                    <li key={f || i} className="flex items-start gap-3 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                      <Check size={16} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </section>

      {model.contentSections && model.contentSections.length > 0 && (
        <section className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)]">
          <div className="container-x py-16 md:py-20">
            <div className="mb-12 max-w-2xl">
              <p className="overline mb-4">— More About This Bar</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                BUILT TO A <span className="text-[var(--brand-primary)]">DIFFERENT STANDARD.</span>
              </h2>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12 gap-y-12">
              {model.contentSections.map((section, i) => (
                <div key={section.heading || i} data-testid={`content-section-${i}`} className="border-t border-[var(--border-default)] pt-6">
                  {section.heading && (
                    <h3 className="font-display text-xl md:text-2xl text-white mb-3">{section.heading}</h3>
                  )}
                  {section.body && (
                    <p className="text-[var(--text-secondary)] text-base md:text-lg leading-relaxed whitespace-pre-line">{section.body}</p>
                  )}
                  {section.bullets && section.bullets.length > 0 && (
                    <ul className="mt-3 space-y-2">
                      {section.bullets.map((b, bi) => (
                        <li key={b || bi} className="flex items-start gap-2 text-base md:text-lg text-[var(--text-secondary)] leading-relaxed">
                          <Check size={14} className="text-[var(--brand-primary)] mt-1 shrink-0" strokeWidth={2.5} />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {related.length > 0 && (
        <section className="container-x pb-16 md:pb-20 border-t border-[var(--border-default)] pt-16 md:pt-20">
          <div className="mb-8">
            <p className="overline mb-3">— Also Fits Your Vehicle</p>
            <h2 className="font-display text-3xl md:text-4xl text-white">
              MORE FOR <span className="text-[var(--brand-primary)]">{vehicle?.label.toUpperCase()}</span>
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((r, i) => (
              <Link key={r.slug} to={`/bull-bars/${r.slug}`} data-testid={`related-bullbar-${i}`} className="group relative block overflow-hidden border border-[var(--border-default)] hover:border-[var(--brand-primary)] transition-colors">
                <div className="aspect-[4/3] overflow-hidden relative">
                  <HoverImage primary={r.thumb} secondary={r.gallery?.[1]?.src} alt={r.name} />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-5">
                    <h3 className="font-display text-xl md:text-2xl text-white leading-tight">{r.name}</h3>
                    <p className="font-mono text-[11px] uppercase tracking-wider text-[var(--brand-primary)] mt-2">
                      {r.priceOnRequest ? "Enquire" : `${r.priceFrom ? "FROM " : ""}$${fmt(r.price)}`}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <RecentlyViewedStrip items={recentlyViewed} />

      <section id="enquire" className="border-t border-[var(--border-default)] bg-[var(--bg-secondary)] scroll-mt-24">
        <div className="container-x py-16 md:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <p className="overline mb-4">— Enquire About This</p>
              <h2 className="font-display text-3xl md:text-5xl text-white leading-[0.95]">
                ASK US<br /><span className="text-[var(--brand-primary)]">ANYTHING.</span>
              </h2>
              <p className="text-[var(--text-secondary)] mt-6 leading-relaxed">
                Vehicle year/spec confirmation, lead times, winch/lighting compatibility, freight to your region — get proper answers.
              </p>
            </div>
            <div className="lg:col-span-7">
              <div className="border border-[var(--border-default)] bg-[var(--bg-main)] p-6 md:p-8">
                <EnquiryForm defaultProduct={`Bull Bar — ${model.name}`} />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}